import React, { useState } from "react";
import { message, Spin, Alert, Typography, theme } from "antd";
import { useSingleItem } from "../_actions/app/items";
import { useExecuteTool } from "./_actions";
import ToolInputForm from "./ToolInputForm";
import ToolOutputRenderer from "./ToolOutputRenderer";

const { Title, Paragraph } = Typography;

function AIToolBox({ tool_id }) {
    const [result, setResult] = useState(null);
    const [status, setStatus] = useState("idle"); // "idle" | "loading" | "done"
    const [lastInputs, setLastInputs] = useState(null);
    const { token } = theme.useToken();

    const {
        data: toolData,
        isLoading: schemaLoading,
        isError: schemaError,
    } = useSingleItem("qdb_ai_tool", `/${tool_id}?fields=*`, !!tool_id);

    const tool = toolData;

    const mutation = useExecuteTool(tool_id, (res) => {
        setResult(res);
        setStatus("done");
    });

    const handleSubmit = (values) => {
        setLastInputs(values);
        setStatus("loading");
        mutation.mutate(
            { inputs: values },
            {
                onError: (err) => {
                    setStatus("idle");
                    const detail =
                        err?.response?.data?.detail ||
                        "Generation failed. Please try again.";
                    message.error(detail);
                },
            },
        );
    };

    const handleReset = () => {
        setResult(null);
        setStatus("idle");
    };

    const handleRegenerate = () => {
        if (!lastInputs) return;
        setStatus("loading");
        mutation.mutate(
            { inputs: lastInputs },
            {
                onError: (err) => {
                    setStatus("done");
                    const detail =
                        err?.response?.data?.detail ||
                        "Generation failed. Please try again.";
                    message.error(detail);
                },
            },
        );
    };

    if (!tool_id) return null;

    if (schemaLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: 48,
                }}
            >
                <Spin />
            </div>
        );
    }

    if (schemaError || !tool) {
        return (
            <Alert
                type="error"
                message="Failed to load tool"
                description="Could not fetch the tool configuration. Please try again."
                showIcon
            />
        );
    }

    if (tool.status !== "active") {
        return (
            <Alert
                type="warning"
                message="Tool not available"
                description={
                    tool.status === "deprecated"
                        ? "This tool has been retired and is no longer available."
                        : "This tool is not currently active."
                }
                showIcon
            />
        );
    }

    return (
        <div style={{ maxWidth: 720, margin: "0 auto", padding: 0 }}>
            {(tool.name || tool.description) && (
                <div style={{ marginBottom: 24 }}>
                    {tool.name && (
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                marginBottom: tool.description ? 4 : 0,
                            }}
                        >
                            {tool.name}
                        </Title>
                    )}
                    {tool.description && (
                        <Paragraph
                            type="secondary"
                            style={{ fontSize: 14, margin: 0 }}
                            ellipsis={{ rows: 1, expandable: true, symbol: "more" }}
                        >
                            {tool.description.replace(/<[^>]+>/g, "")}
                        </Paragraph>
                    )}
                </div>
            )}

            {status !== "done" && (
                <ToolInputForm
                    inputs={tool.inputs || []}
                    onSubmit={handleSubmit}
                    loading={status === "loading"}
                />
            )}

            {status === "done" && result && (
                <ToolOutputRenderer
                    result={result}
                    output_config={tool.output_config}
                    onReset={handleReset}
                    onRegenerate={handleRegenerate}
                    isRegenerating={status === "loading"}
                />
            )}
        </div>
    );
}

export default AIToolBox;
